/**
 * Bundle studio.
 *
 * The rail is filled from the real Shopify cart, never from a total worked out
 * here. The bundle savings are automatic discounts applied by Shopify, so the
 * cart is the only thing that knows what a customer will actually be charged.
 * A figure calculated in the browser would drift the moment a tier changed.
 */
class BundleStudio extends HTMLElement {
  connectedCallback() {
    const raw = this.querySelector('[data-studio-data]');
    try {
      this.data = JSON.parse(raw.textContent);
    } catch (error) {
      // Without the data the picker can't work; leave the markup inert rather
      // than throwing on every interaction.
      console.error('Bundle studio: could not read its data', error);
      return;
    }

    this.byVariantId = new Map();
    this.byProductId = new Map();
    for (const group of this.data.groups) {
      for (const product of group.products) {
        this.byProductId.set(String(product.id), { ...product, kind: group.kind });
        for (const variant of product.variants) {
          this.byVariantId.set(String(variant.id), { ...variant, product, kind: group.kind });
        }
      }
    }

    this.lines = this.querySelector('[data-lines]');
    this.chooser = this.querySelector('[data-chooser]');
    this.busy = false;

    this.addEventListener('click', this.onClick.bind(this));
    this.refresh();
  }

  /* ---------------------------------------------------------------- events */

  onClick(event) {
    const filter = event.target.closest('[data-filter]');
    if (filter) return this.applyFilter(filter);

    const add = event.target.closest('[data-add]');
    if (add) return this.onAdd(add.dataset.add);

    const chooserAdd = event.target.closest('[data-chooser-add]');
    if (chooserAdd) return this.onChooserAdd();

    const shape = event.target.closest('[data-shape]');
    if (shape) return this.selectShape(shape);

    if (event.target.closest('[data-chooser-close]') || event.target === this.chooser) {
      return this.closeChooser();
    }

    const step = event.target.closest('[data-step-line]');
    if (step) {
      return this.changeLine(step.dataset.key, Number(step.dataset.stepLine));
    }

    const remove = event.target.closest('[data-remove-line]');
    if (remove) return this.setLine(remove.dataset.key, 0);
  }

  applyFilter(button) {
    const tag = button.dataset.filter;
    this.querySelectorAll('[data-filter]').forEach((b) => b.classList.toggle('is-active', b === button));
    this.querySelectorAll('[data-kind="nail"]').forEach((card) => {
      const tags = (card.dataset.tags || '').split(',');
      card.hidden = tag !== '' && !tags.includes(tag);
    });
  }

  /* ------------------------------------------------------------- selection */

  onAdd(productId) {
    const product = this.byProductId.get(String(productId));
    if (!product) return;

    const sellable = product.variants.filter((v) => v.available);
    if (sellable.length === 0) return;

    // One shape means there is nothing to choose: add it and keep moving.
    if (sellable.length === 1) return this.addVariant(sellable[0].id);

    this.openChooser(product, sellable);
  }

  openChooser(product, variants) {
    this.chooserProduct = product;
    this.chooserVariantId = variants[0].id;

    this.chooser.querySelector('[data-chooser-image]').src = product.image || '';
    this.chooser.querySelector('[data-chooser-title]').textContent = product.styleName;
    this.chooser.querySelector('[data-chooser-meta]').textContent = [
      product.collectionWord ? `${product.collectionWord} Collection` : '',
      this.money(product.variants[0].priceCents),
    ]
      .filter(Boolean)
      .join('   |   ');

    const shapes = this.chooser.querySelector('[data-chooser-shapes]');
    shapes.innerHTML = '';
    for (const variant of variants) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bundle-studio__shape';
      button.dataset.shape = variant.id;
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', String(variant.id === this.chooserVariantId));
      button.classList.toggle('is-selected', variant.id === this.chooserVariantId);
      // Each shape has its own photograph; the product's featured image would
      // show an almond nail on the square option.
      button.innerHTML =
        `<img src="${variant.image || product.image || ''}" alt="" loading="lazy">` +
        `<span>${variant.title}</span>`;
      shapes.appendChild(button);
    }

    this.chooser.hidden = false;
    document.body.classList.add('bundle-studio-open');
  }

  selectShape(button) {
    this.chooserVariantId = Number(button.dataset.shape);
    this.chooser.querySelectorAll('[data-shape]').forEach((b) => {
      const on = b === button;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-checked', String(on));
    });
    const variant = this.byVariantId.get(String(this.chooserVariantId));
    const image = variant?.image || this.chooserProduct?.image;
    if (image) this.chooser.querySelector('[data-chooser-image]').src = image;
  }

  onChooserAdd() {
    const id = this.chooserVariantId;
    this.closeChooser();
    if (id) this.addVariant(id);
  }

  closeChooser() {
    this.chooser.hidden = true;
    document.body.classList.remove('bundle-studio-open');
  }

  /* ------------------------------------------------------------------ cart */

  async addVariant(variantId) {
    const variant = this.byVariantId.get(String(variantId));
    if (!variant) return;

    // The bundle prices only run to a set number of sets. Past that a customer
    // would quietly pay more than the advertised bundle price, so stop here
    // rather than let them walk past the top tier.
    const cap = variant.kind === 'nail' ? this.data.maxNails : this.data.maxLashes;
    const current = variant.kind === 'nail' ? this.nailCount : this.lashCount;
    if (current >= cap) {
      return this.flashCap(variant.kind, cap);
    }

    await this.post('/cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
    this.refresh();
  }

  changeLine(key, delta) {
    const line = (this.cart?.items || []).find((i) => i.key === key);
    if (!line) return;
    this.setLine(key, line.quantity + delta);
  }

  async setLine(key, quantity) {
    if (quantity < 0) return;
    const line = (this.cart?.items || []).find((i) => i.key === key);
    if (line && quantity > line.quantity) {
      const kind = this.byVariantId.get(String(line.variant_id))?.kind;
      const cap = kind === 'nail' ? this.data.maxNails : this.data.maxLashes;
      const current = kind === 'nail' ? this.nailCount : this.lashCount;
      if (current >= cap) return this.flashCap(kind, cap);
    }
    await this.post('/cart/change.js', { id: key, quantity });
    this.refresh();
  }

  async post(url, body) {
    if (this.busy) return;
    this.busy = true;
    this.classList.add('is-busy');
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        // Most often this is Shopify refusing to oversell, which is the
        // correct answer: show what it said rather than a generic failure.
        const detail = await response.json().catch(() => ({}));
        this.notify(detail.description || detail.message || 'That could not be added.');
      }
    } catch (error) {
      this.notify('Could not reach the cart. Please try again.');
    } finally {
      this.busy = false;
      this.classList.remove('is-busy');
    }
  }

  async refresh() {
    try {
      this.cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    } catch (error) {
      return;
    }
    this.render();
  }

  /* --------------------------------------------------------------- display */

  get bundleItems() {
    return (this.cart?.items || []).filter((i) => this.byVariantId.has(String(i.variant_id)));
  }

  get nailCount() {
    return this.bundleItems
      .filter((i) => this.byVariantId.get(String(i.variant_id)).kind === 'nail')
      .reduce((n, i) => n + i.quantity, 0);
  }

  get lashCount() {
    return this.bundleItems
      .filter((i) => this.byVariantId.get(String(i.variant_id)).kind === 'lash')
      .reduce((n, i) => n + i.quantity, 0);
  }

  money(cents) {
    const value = (cents / 100).toFixed(2);
    const format = this.data.moneyFormat || '${{amount}}';
    // The shop's format string is the source of truth for the symbol, but only
    // the plain amount placeholder is worth honouring here.
    return format.replace(/\{\{\s*amount[^}]*\}\}/, value);
  }

  render() {
    const items = this.bundleItems;
    const count = items.reduce((n, i) => n + i.quantity, 0);

    this.querySelector('[data-count]').textContent = count
      ? `${count} ${count === 1 ? 'item' : 'items'}`
      : '';
    this.querySelector('[data-rail-empty]').hidden = count > 0;
    this.querySelector('[data-totals]').hidden = count === 0;
    this.querySelector('[data-checkout]').classList.toggle('is-disabled', count === 0);

    this.lines.innerHTML = '';
    for (const item of items) {
      const known = this.byVariantId.get(String(item.variant_id));
      const shape = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      const li = document.createElement('li');
      li.className = 'bundle-studio__line';
      li.innerHTML = `
        <img class="bundle-studio__line-image" src="${known.image || known.product.image || ''}" alt="" loading="lazy">
        <div class="bundle-studio__line-info">
          <p class="bundle-studio__line-name">${known.product.styleName}</p>
          ${known.product.collectionWord ? `<p class="bundle-studio__line-meta">${known.product.collectionWord} Collection</p>` : ''}
          ${shape ? `<p class="bundle-studio__line-meta">${shape}</p>` : ''}
          <p class="bundle-studio__line-price">${this.money(item.original_price)}</p>
          <div class="bundle-studio__stepper">
            <button type="button" data-step-line="-1" data-key="${item.key}" aria-label="Reduce quantity">&minus;</button>
            <span>${item.quantity}</span>
            <button type="button" data-step-line="1" data-key="${item.key}" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <button type="button" class="bundle-studio__line-remove" data-remove-line data-key="${item.key}" aria-label="Remove">
          &times;
        </button>`;
      this.lines.appendChild(li);
    }

    const subtotal = items.reduce((n, i) => n + i.original_line_price, 0);
    const discount = this.cart.total_discount || 0;

    this.querySelector('[data-subtotal]').textContent = this.money(subtotal);
    this.querySelector('[data-total]').textContent = this.money(this.cart.total_price);

    const discountRow = this.querySelector('[data-discount-row]');
    discountRow.hidden = discount === 0;
    if (discount > 0) {
      this.querySelector('[data-discount]').textContent = `− ${this.money(discount)}`;
    }

    // Keep the phone's sticky summary in step with the rail.
    const bar = this.querySelector('[data-bar]');
    if (bar) {
      bar.hidden = count === 0;
      this.querySelector('[data-bar-count]').textContent = count
        ? `${count} ${count === 1 ? 'item' : 'items'}`
        : '';
      this.querySelector('[data-bar-total]').textContent = this.money(this.cart.total_price);
    }

    // Name the offer the customer actually got, straight from the cart.
    const saving = this.querySelector('[data-saving]');
    const titles = new Set();
    for (const application of this.cart.cart_level_discount_applications || []) titles.add(application.title);
    for (const item of this.cart.items) for (const d of item.discounts || []) titles.add(d.title);
    if (discount > 0 && titles.size > 0) {
      saving.hidden = false;
      this.querySelector('[data-saving-title]').textContent = [...titles][0];
      this.querySelector('[data-saving-text]').textContent = `You've saved ${this.money(discount)}.`;
    } else {
      saving.hidden = true;
    }
  }

  flashCap(kind, cap) {
    this.notify(
      `That's the most we can bundle in one order - ${cap} ${kind === 'nail' ? 'nail' : 'lash'} sets. ` +
        `Place a second order for more.`,
    );
  }

  notify(message) {
    let el = this.querySelector('[data-studio-note]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'bundle-studio__message';
      el.setAttribute('data-studio-note', '');
      el.setAttribute('role', 'status');
      this.querySelector('.bundle-studio__rail-inner').prepend(el);
    }
    el.textContent = message;
    clearTimeout(this.noteTimer);
    this.noteTimer = setTimeout(() => el.remove(), 6000);
  }
}

if (!customElements.get('bundle-studio')) {
  customElements.define('bundle-studio', BundleStudio);
}
